import React, { useState, useContext, useEffect } from 'react';
import axios from 'axios';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import Header from '../../components/Header';
import { useParams } from 'react-router-dom';
import dateUtils from '../../utilities/dateUtils';
import {
  getOrderRequest,
  downloadMaterial,
  updateOrder,
  uploadFileToOrder,
  updateFileToOrder,
  deleteFileToOrder,
  downloadUserAttachment,
} from '../../api/Api';
import { getFileType, getFileTypeName } from '../../utilities/parsers';
import GeneralTab from '../../components/OrderTabs/GeneralTab';
import AttachmentsTab from '../../components/OrderTabs/AttachmentsTab';
import ConciliacionTab from '../../components/OrderTabs/ConciliacionTab';
import ObservationsTab from '../../components/OrderTabs/ObservationsTab';
import UnauthorizedSection from '../../components/Unauthorized';
import EditableFields from '../../components/OrderTabs/EditableFields';
import Divider from '../../components/Divider';
import { useToasts } from 'react-toast-notifications';
import { ProfileContext } from '../../contexts/ProfileContext';
import { useLoadingContext } from '../../contexts/LoadingContext';
import { useUploadQueueContext } from '../../contexts/UploadQueueContext';

const OrderPage = () => {
  const { id } = useParams();
  const [order, setOrder] = useState({});
  const [activeTab, setActiveTab] = useState('general');
  const [orderNumber, setOrderNumber] = useState([]);
  const [fromTo, setFromTo] = useState([]);
  const [subtotal, setSubtotal] = useState(0);
  const [files, setFiles] = useState([]);
  const [conciliationFiles, setConciliationFiles] = useState([]);
  const [observationsOrder, setObservationsOrder] = useState([]);
  const [orderFiles, setOrderFiles] = useState([]);
  const [fileToUpload, setFileToUpload] = useState([]);
  const [fileToUploadProg, setFileToUploadProg] = useState([]);
  const [fileToUploadTrans, setFileToUploadTrans] = useState([]);
  const [fileToUploadTest, setFileToUploadTest] = useState([]);
  const [fileDescription, setFileDescription] = useState('');
  const [fileDescriptionProg, setFileDescriptionProg] = useState('');
  const [fileDescriptionTrans, setFileDescriptionTrans] = useState('');
  const [fileDescriptionTest, setFileDescriptionTest] = useState('');
  const [fileType, setFileType] = useState(0);
  const [cintaTestigo, setCintaTestigo] = useState('');
  const [observations, setObservations] = useState('');
  const [mmassFTP, setMmassFTP] = useState('');
  const [orderDjango, setOrderDjango] = useState({});
  const [errorMsg, setErrorMsg] = useState('');
  const [errorDownload, setErrorDownload] = useState(false);
  const { addToast } = useToasts();
  const { profile, isLoadingProfile } = useContext(ProfileContext);
  const [materials, setMaterials] = useState([]);
  const [agency, setAgency] = useState('');
  const [materialsStatus, setMaterialsStatus] = useState([]);
  const [affiliatesData, setAffiliatesData] = useState([]);
  const [results, setResults] = useState([]);
  const [djangoRows, setDjangoRows] = useState({
    months: [],
    material_rows: [],
  });
  const [nonExistingUsers, setNonExistingUsers] = useState([]);

  const FTP_MATERIALS_FOLDER = process.env.REACT_APP_ATTACHMENTS_FTP_MATERIALS_FOLDER;
  const FTP_IP = process.env.REACT_APP_FTP_IP;
  const FTP_PORT = process.env.REACT_APP_FTP_PORT;
  const SHARED_APP_IP_DOWNLOAD = process.env.REACT_APP_IP_DOWNLOAD;
  const SHARED_APP_IP_UPLOAD = process.env.REACT_APP_IP_UPLOAD;
  const SHARED_APP_PORT_DOWNLOAD = process.env.REACT_APP_PORT_DOWNLOAD;
  const FTP_USER = process.env.REACT_APP_FTP_USER;
  const FTP_PASS = process.env.REACT_APP_FTP_PASS;

  const { setLoading } = useLoadingContext();
  const { addToQueue } = useUploadQueueContext();

  const getOrder = async () => {
    setLoading(true);
    const response = await getOrderRequest(id);
    if (response.Error) {
      setErrorMsg('Unauthorized');
      setLoading(false);
      return;
    }
    const {
      results: lineResults,
      files,
      conciliation_files,
      affiliate_notes,
      ftp,
      orderFiles,
      order,
      materials: mats,
      users_status: statusForAdmin,
      rows: rowsDjango,
      child_medias_without_users: childMediasWithoutUsers,
      observations: observationsOrder,
    } = response;
    setResults(lineResults);
    setDjangoRows(rowsDjango);
    setOrderFiles(orderFiles);
    setMmassFTP(ftp);
    setFiles(files);
    setConciliationFiles(conciliation_files);
    setObservationsOrder(observationsOrder);
    setOrder(lineResults[0]);
    const { fecha: orderDate } = lineResults[0];
    setOrderDjango(order);
    setAffiliatesData(affiliate_notes);
    setCintaTestigo(affiliate_notes.cinta_testigo);
    setObservations(affiliate_notes.observations);
    setMaterials(mats);
    setMaterialsStatus(statusForAdmin);
    const vigencia = `${dateUtils.getFormattedDate(
      lineResults[0].fecha
    )} - ${dateUtils.getFormattedDate(lineResults[0].fecha)}`.split('-');
    setFromTo(vigencia);
    const lines = [...new Set(lineResults.map((order) => order.id_detalle))];
    let detailsData = lines.map((line) => lineResults.filter((order) => order.id_detalle === line));
    if (!profile?.is_admin) {
      // Filtramos los detalles que tengan material
      detailsData = detailsData.filter((detail) =>
        mats.find((mat) => mat.id_detalle === detail[0].id_detalle)
      );
    }

    const montosNetos = [];
    setSubtotal(montosNetos.reduce((sum, current) => sum + current, 0));
    if (detailsData.length > 0) {
      const firstDetail = detailsData[0];
      const agency = firstDetail[0]?.Agencia;
      setAgency(agency);
    }

    setNonExistingUsers(childMediasWithoutUsers);
    setLoading(false);
  };

  useEffect(() => {
    const callGetOrder = async () => {
      await getOrder();
    };
    setOrderNumber(id.split('-'));
    callGetOrder();
  }, [id]);

  const downloadFile = async (e, file) => {
    e.preventDefault();
    setLoading(true);
    const { name, media } = file;
    const parsedMediaName = media.replace(/\*/g, '');
    const parsedOrderNumber = `${orderNumber[0]}-${orderNumber[1]}-${orderNumber[2]}`;
    const fileType = file.type;
    const fileTypeName = await getFileTypeName(fileType);
    const credentials = btoa(`${FTP_USER}:${FTP_PASS}`);

    const fileUrl = `${SHARED_APP_IP_DOWNLOAD}/${FTP_MATERIALS_FOLDER}/${parsedOrderNumber}/${parsedMediaName}/${fileTypeName}/${name}`;

    axios
      .get(fileUrl, {
        headers: {
          Authorization: `Basic ${credentials}`,
        },
        responseType: 'blob',
      })
      .then((response) => {
        const contentType = response.headers['content-type'] || '';
        if (contentType.includes('text/html') || contentType.includes('text/plain')) {
          throw new Error('Tipo de archivo incorrecto, error de server');
        }

        // Crear un enlace para descargar el archivo
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', name); // Nombre del archivo para descargar
        document.body.appendChild(link);
        link.click();
        URL.revokeObjectURL(url);

        addToast('Archivo Descargado con éxito', {
          appearance: 'success',
          autoDismiss: true,
          autoDismissTimeout: 3000,
          placement: 'bottom-center',
        });

        return downloadUserAttachment(id, {
          file,
          name,
          content_type: getFileType(name),
          media,
          id,
        });
      })
      .then((response) => {
        if (response.ok) {
          setLoading(false);
        } else {
          setLoading(false);
          addToast(
            'Hubo un error al procesar la informacion de descarga. Por favor contactese con un administrador',
            {
              appearance: 'error',
              autoDismiss: true,
              autoDismissTimeout: 5000,
              placement: 'bottom-center',
            }
          );
        }
      })
      .catch(async (error) => {
        console.log(error);
        setLoading(false);
        addToast(
          'El archivo no existe o hay algun problema de servidor. Intentando descarga por servidor',
          {
            appearance: 'error',
            autoDismiss: true,
            autoDismissTimeout: 5000,
            placement: 'bottom-center',
          }
        );
        setLoading(true);
        const isFtp = true;
        const response = await downloadUserAttachment(id, {
          file,
          name,
          content_type: getFileType(name),
          media,
          id,
          isFtp,
        });
        if (response.ok) {
          const blob = await response.blob();
          let url = window.URL.createObjectURL(blob);
          let a = document.createElement('a');
          a.href = url;
          a.download = name;
          a.click();
          addToast('Archivo Descargado con éxito', {
            appearance: 'success',
            autoDismiss: true,
            autoDismissTimeout: 3000,
            placement: 'bottom-center',
          });
          await getOrder();
          setLoading(false);
        } else {
          addToast(
            'El archivo no existe o hay algun problema de servidor. Por favor contactese con un administrador',
            {
              appearance: 'error',
              autoDismiss: true,
              autoDismissTimeout: 5000,
              placement: 'bottom-center',
            }
          );
          setLoading(false);
        }
        setLoading(false);
      });
  };

  const notValidName = async (name) => {
    /**
     Funcion que valida caracteres especiales en el nombre
     * 
     */

    const regex = /^[a-zA-Z0-9_\-.,() ]+(\.[a-zA-Z0-9]+)?$/;

    if (regex.test(name)) {
      return false;
    } else {
      return true;
    }
  };

  const handleUpdateFile = async (file) => {
    setLoading(true);
    try {
      await updateFileToOrder(file).then((response) => {
        if (response.ok) {
          addToast('Archivo Aprobado con éxito', {
            appearance: 'success',
            autoDismiss: true,
            autoDismissTimeout: 3000,
            placement: 'bottom-center',
          });
        } else {
          addToast('Ocurrió un error, por favor intente nuevamente o contacte a su administrador', {
            appearance: 'error',
            autoDismiss: true,
            autoDismissTimeout: 3000,
            placement: 'bottom-center',
          });
        }
      });
      await getOrder();
    } catch (error) {
      // Manejar el error aquí, por ejemplo, mostrar un mensaje de error o registrar el error en la consola
      addToast('Ocurrió un error, por favor intente nuevamente o contacte a su administrador', {
        appearance: 'error',
        autoDismiss: true,
        autoDismissTimeout: 3000,
        placement: 'bottom-center',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteFile = async (file, observations) => {
    setLoading(true);
    try {
      await deleteFileToOrder(file, observations);
      await getOrder();
    } catch (error) {
      // Manejar el error aquí, por ejemplo, mostrar un mensaje de error o registrar el error en la consola
      console.log(error);
    } finally {
      addToast('Archivo Rechazado con éxito', {
        appearance: 'success',
        autoDismiss: true,
        autoDismissTimeout: 3000,
        placement: 'bottom-center',
      });
      setLoading(false);
    }
  };

  const handleUpload = async (fileToUpload, fileDescription, fileType, profile) => {
    try {
      const parsedMediaName = profile.media.name;
      const parsedOrderNumber = `${orderNumber[0]}-${orderNumber[1]}-${orderNumber[2]}`;

      // Validación del nombre del archivo
      const isNotValidName = await notValidName(fileToUpload[0].name);
      if (isNotValidName) {
        addToast(
          'El nombre del archivo contiene caracteres especiales, por favor revise e intente nuevamente',
          {
            appearance: 'error',
            autoDismiss: true,
            autoDismissTimeout: 3000,
            placement: 'bottom-center',
          }
        );
        return;
      }

      // Limpiar formulario inmediatamente
      setFileDescription('');
      setFileDescriptionProg('');
      setFileDescriptionTrans('');
      setFileDescriptionTest('');
      setFileToUpload([]);
      setFileToUploadProg([]);
      setFileToUploadTrans([]);
      setFileToUploadTest([]);
      setFileType(0);

      // Agregar a la cola de uploads
      addToQueue({
        fileToUpload,
        fileDescription,
        fileType,
        profile,
        parsedOrderNumber,
        orderId: id,
        getOrder, // Pasar la función para refrescar después de la carga
      });

      addToast('Archivo agregado a la cola de carga', {
        appearance: 'info',
        autoDismiss: true,
        autoDismissTimeout: 2000,
        placement: 'bottom-center',
      });
    } catch (error) {
      console.error('Error al agregar archivo a la cola:', error);
      addToast('Error al procesar el archivo', {
        appearance: 'error',
        autoDismiss: true,
        autoDismissTimeout: 3000,
        placement: 'bottom-center',
      });
    }
  };

  const onClickOrderAttachment = async (e, link, name, attachment_id) => {
    e.preventDefault();
    setLoading(true);
    const format_name = name;
    const credentials = window.btoa(`${FTP_USER}:${FTP_PASS}`);
    const fileUrl = `${SHARED_APP_IP_DOWNLOAD}/${FTP_MATERIALS_FOLDER}/${format_name}`;

    axios
      .get(fileUrl, {
        headers: {
          Authorization: `Basic ${credentials}`,
        },
        responseType: 'blob',
      })
      .then((response) => {
        const contentType = response.headers['content-type'] || '';
        if (contentType.includes('text/html') || contentType.includes('text/plain')) {
          throw new Error('Tipo de archivo incorrecto, error de server');
        }

        // Crear un enlace para descargar el archivo
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', format_name); // Nombre del archivo para descargar
        document.body.appendChild(link);
        link.click();
        URL.revokeObjectURL(url);

        addToast('Archivo Descargado con éxito', {
          appearance: 'success',
          autoDismiss: true,
          autoDismissTimeout: 3000,
          placement: 'bottom-center',
        });

        return downloadMaterial(id, {
          name,
          link,
          content_type: getFileType(name),
          attachment_id,
        });
      })
      .then(async (response) => {
        if (response.ok) {
          await getOrder();
          setLoading(false);
        } else {
          setLoading(false);
          addToast(
            'Hubo un error al procesar la informacion de descarga. Por favor contactese con un administrador',
            {
              appearance: 'error',
              autoDismiss: true,
              autoDismissTimeout: 5000,
              placement: 'bottom-center',
            }
          );
        }
      })
      .catch(async (error) => {
        setLoading(false);
        addToast(
          'El archivo no existe o hay algun problema de servidor. Intentando descarga por servidor',
          {
            appearance: 'error',
            autoDismiss: true,
            autoDismissTimeout: 5000,
            placement: 'bottom-center',
          }
        );
        setLoading(true);
        const isFtp = true;

        const response = await downloadMaterial(id, {
          name,
          link,
          content_type: getFileType(name),
          attachment_id,
          isFtp,
        });
        if (response.ok) {
          const blob = await response.blob();
          let url = window.URL.createObjectURL(blob);
          let a = document.createElement('a');
          a.href = url;
          a.download = name;
          a.click();
          addToast('Archivo Descargado con éxito', {
            appearance: 'success',
            autoDismiss: true,
            autoDismissTimeout: 3000,
            placement: 'bottom-center',
          });
          await getOrder();
          setLoading(false);
        } else {
          addToast(
            'El archivo no existe o hay algun problema de servidor. Por favor contactese con un administrador',
            {
              appearance: 'error',
              autoDismiss: true,
              autoDismissTimeout: 5000,
              placement: 'bottom-center',
            }
          );
          setLoading(false);
        }
      });

    setLoading(false);
  };

  const onClickSaveChanges = async () => {
    setLoading(true);
    try {
      await updateOrder(id, {
        cintaTestigo,
        observations,
      });
      addToast('Cambios guardados!', {
        appearance: 'success',
        autoDismiss: true,
        autoDismissTimeout: 5000,
        placement: 'bottom-center',
      });
    } catch (error) {
      addToast('Hubo un problema, no pudimos guardar los cambios', {
        appearance: 'error',
        autoDismiss: true,
        autoDismissTimeout: 5000,
        placement: 'bottom-center',
      });
    }
    setLoading(false);
  };

  const processOrderComplete = async () => {
    setLoading(true);

    const fileNames = djangoRows.material_rows.map((row) => row.file_name);

    const zip = new JSZip();
    const folder = zip.folder('materiales');
    let anyDownload = false;
    const successfullyDownloaded = [];

    try {
      const filePromises = fileNames.map(async (name) => {
        const format_name = name;
        const credentials = window.btoa(`${FTP_USER}:${FTP_PASS}`);
        const fileUrl = `${SHARED_APP_IP_DOWNLOAD}/${FTP_MATERIALS_FOLDER}/${format_name}`;

        const response = await axios.get(fileUrl, {
          headers: {
            Authorization: `Basic ${credentials}`,
          },
          responseType: 'blob',
        });

        if (response.status !== 200) {
          addToast(`Error al descargar el archivo: ${name}`, {
            appearance: 'error',
            autoDismiss: true,
            autoDismissTimeout: 5000,
            placement: 'bottom-center',
          });
          return;
        }
        anyDownload = true;
        const blob = response.data;
        folder.file(name, blob);
        successfullyDownloaded.push(name);
      });

      await Promise.all(filePromises);
      if (anyDownload) {
        const content = await zip.generateAsync({ type: 'blob' });
        saveAs(content, 'materiales.zip');
      }

      for (const name of successfullyDownloaded) {
        const downloadResponse = await downloadMaterial(id, {
          name,
        });

        if (downloadResponse.ok) {
          addToast(`Procesamiento material ${name} completado!`, {
            appearance: 'success',
            autoDismiss: true,
            autoDismissTimeout: 5000,
            placement: 'bottom-center',
          });
        } else {
          addToast(
            `Error al procesar material : ${name}. Por favor contactese con un administrador o intente su descarga individual`,
            {
              appearance: 'error',
              autoDismiss: true,
              autoDismissTimeout: 5000,
              placement: 'bottom-center',
            }
          );
        }
      }
    } catch (error) {
      addToast(
        `Error al procesar orden completa. Por favor contactese con un administrador o intente descarga individual`,
        {
          appearance: 'error',
          autoDismiss: true,
          autoDismissTimeout: 5000,
          placement: 'bottom-center',
        }
      );
    } finally {
      await getOrder();
      setLoading(false);
    }
  };

  // Función helper para obtener icono de tab
  const getTabIcon = (tabName) => {
    const icons = {
      general: 'fas fa-home',
      attachments: 'fas fa-paperclip',
      conciliacion: 'fas fa-balance-scale',
      observations: 'fas fa-eye'
    };
    return icons[tabName] || 'fas fa-file';
  };

  const MainPage = () => {
    if (errorMsg) {
      return <UnauthorizedSection />;
    }
    
    return (
      <div className="box has-background-white-bis" style={{ margin: 0 }}>
        {(() => {
          switch (activeTab) {
            case 'general':
              return (
                <GeneralTab
                  orderNumber={orderNumber}
                  order={order}
                  fromTo={fromTo}
                  id={id}
                  subtotal={subtotal}
                  orderFiles={orderFiles}
                  mmassFTP={mmassFTP}
                  onClickOrderAttachment={onClickOrderAttachment}
                  orderDjango={orderDjango}
                  materials={materials}
                  agency={agency}
                  materialsStatus={materialsStatus}
                  results={results}
                  djangoRows={djangoRows}
                  nonExistingUsers={nonExistingUsers}
                  downloadFile={downloadFile}
                  processOrderComplete={processOrderComplete}
                  handleUpdateFile={handleUpdateFile}
                  handleDeleteFile={handleDeleteFile}
                  orderId={id}
                />
              );
            case 'attachments':
              return (
                <AttachmentsTab
                  orderNumber={orderNumber}
                  order={order}
                  fileToUpload={fileToUpload}
                  setFileToUpload={setFileToUpload}
                  fileDescription={fileDescription}
                  setFileDescription={setFileDescription}
                  cintaTestigo={cintaTestigo}
                  setCintaTestigo={setCintaTestigo}
                  observations={observations}
                  setObservations={setObservations}
                  handleUpload={handleUpload}
                  files={files}
                  onClickSaveChanges={onClickSaveChanges}
                  profile={profile}
                  orderId={id}
                />
              );
            case 'conciliacion':
              return (
                <ConciliacionTab
                  orderNumber={orderNumber}
                  order={order}
                  fileToUploadProg={fileToUploadProg}
                  setFileToUploadProg={setFileToUploadProg}
                  fileToUploadTrans={fileToUploadTrans}
                  setFileToUploadTrans={setFileToUploadTrans}
                  fileToUploadTest={fileToUploadTest}
                  setFileToUploadTest={setFileToUploadTest}
                  fileDescriptionProg={fileDescriptionProg}
                  setFileDescriptionProg={setFileDescriptionProg}
                  fileDescriptionTrans={fileDescriptionTrans}
                  setFileDescriptionTrans={setFileDescriptionTrans}
                  fileDescriptionTest={fileDescriptionTest}
                  setFileDescriptionTest={setFileDescriptionTest}
                  setFileType={setFileType}
                  handleUpload={handleUpload}
                  downloadFile={downloadFile}
                  files={conciliationFiles}
                  profile={profile}
                  orderId={id}
                />
              );
            case 'observations':
              return (
                <ObservationsTab
                  orderNumber={orderNumber}
                  order={order}
                  fromTo={fromTo}
                  agency={agency}
                  orderDjango={orderDjango}
                  observationsOrder={observationsOrder}
                />
              );
            default:
              return null;
          }
        })()}
      </div>
    );
  };

  if (isLoadingProfile) {
    return (
      <div className="hero is-fullheight">
        <div className="hero-body">
          <div className="container is-fluid px-5 has-text-centered">
            <div className="box has-shadow">
              <progress className="progress is-primary is-large" max="100">45%</progress>
              <p className="subtitle is-5 mt-3">Cargando información...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="has-background-light min-height-100vh">
      <Header />
      
      {!errorMsg && (
        <div className="hero is-primary is-small">
          <div className="hero-body py-4">
            <div className="container is-fluid px-5">
              <div className="level">
                <div className="level-left">
                  <div className="level-item">
                    <div>
                      <h1 className="title is-4 has-text-white mb-1">
                        <span className="icon mr-2">
                          <i className="fas fa-file-invoice"></i>
                        </span>
                        Orden #{orderNumber.join('-')}
                      </h1>
                      <p className="subtitle is-6 has-text-white-ter mb-0">
                        {agency && (
                          <>
                            <span className="icon is-small mr-1">
                              <i className="fas fa-building"></i>
                            </span>
                            {agency}
                          </>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="section py-4">
        <div className="container is-fluid px-5">
          {!errorMsg && (
            <div className="card mb-4">
              <div className="card-content p-0">
                <div className="tabs is-boxed is-medium mb-0">
                  <ul>
                    <li className={activeTab === 'general' ? 'is-active' : ''}>
                      <a 
                        onClick={() => setActiveTab('general')}
                        className="has-text-weight-semibold"
                      >
                        <span className="icon is-small mr-2">
                          <i className={getTabIcon('general')}></i>
                        </span>
                        <span>General</span>
                      </a>
                    </li>
                    {/* {profile?.is_admin && (
                      <li className={activeTab === 'attachments' ? 'is-active' : ''}>
                        <a 
                          onClick={() => setActiveTab('attachments')}
                          className="has-text-weight-semibold"
                        >
                          <span className="icon is-small mr-2">
                            <i className={getTabIcon('attachments')}></i>
                          </span>
                          <span>Adjuntos</span>
                        </a>
                      </li>
                    )} */}
                    {!profile?.is_admin && (
                      <li className={activeTab === 'conciliacion' ? 'is-active' : ''}>
                        <a 
                          onClick={() => setActiveTab('conciliacion')}
                          className="has-text-weight-semibold"
                        >
                          <span className="icon is-small mr-2">
                            <i className={getTabIcon('conciliacion')}></i>
                          </span>
                          <span>Arch. Conciliación</span>
                        </a>
                      </li>
                    )}
                    <li className={activeTab === 'observations' ? 'is-active' : ''}>
                      <a 
                        onClick={() => setActiveTab('observations')}
                        className="has-text-weight-semibold"
                      >
                        <span className="icon is-small mr-2">
                          <i className={getTabIcon('observations')}></i>
                        </span>
                        <span>Observaciones</span>
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          <MainPage />

          {!errorMsg && activeTab === 'attachments' && !profile?.is_admin && (
            <div className="card mt-4">
              <header className="card-header">
                <p className="card-header-title">
                  <span className="icon mr-2">
                    <i className="fas fa-edit"></i>
                  </span>
                  Campos Editables
                </p>
              </header>
              <div className="card-content">
                <EditableFields
                  disable={profile?.is_admin}
                  cintaTestigo={cintaTestigo}
                  setCintaTestigo={setCintaTestigo}
                  observations={observations}
                  setObservations={setObservations}
                />
                <Divider />
                <div className="field is-grouped is-grouped-right">
                  <div className="control">
                    <button 
                      className="button is-primary is-medium has-text-weight-semibold"
                      onClick={onClickSaveChanges}
                    >
                      <span className="icon mr-2">
                        <i className="fas fa-save"></i>
                      </span>
                      <span>Guardar Cambios</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {!errorMsg && activeTab === 'attachments' && profile?.is_admin && (
            <div className="card mt-4">
              <header className="card-header">
                <p className="card-header-title">
                  <span className="icon mr-2">
                    <i className="fas fa-info-circle"></i>
                  </span>
                  Observaciones y Cintas Testigo
                </p>
              </header>
              <div className="card-content">
                <div className="table-container">
                  <table className="table is-bordered is-striped is-hoverable is-fullwidth">
                    <thead>
                      <tr className="has-background-primary">
                        <th className="has-text-white">
                          <span className="icon-text">
                            <span className="icon">
                              <i className="fas fa-broadcast-tower"></i>
                            </span>
                            <span>Medio</span>
                          </span>
                        </th>
                        <th className="has-text-white">
                          <span className="icon-text">
                            <span className="icon">
                              <i className="fas fa-tape"></i>
                            </span>
                            <span>Cinta Testigo</span>
                          </span>
                        </th>
                        <th className="has-text-white">
                          <span className="icon-text">
                            <span className="icon">
                              <i className="fas fa-comment-alt"></i>
                            </span>
                            <span>Observaciones</span>
                          </span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {affiliatesData.map((media, index) => (
                        <tr key={`${index}`} className="is-size-7">
                          <td className="has-text-weight-semibold">{media.media}</td>
                          <td>
                            {media.cinta_testigo && (
                              <div className="content">
                                <ul className="mb-0">
                                  {media.cinta_testigo.split(',').map((item, itemIndex) => (
                                    <li key={itemIndex} className="mb-1">
                                      <a 
                                        href={item} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="has-text-link"
                                      >
                                        <span className="icon is-small mr-1">
                                          <i className="fas fa-external-link-alt"></i>
                                        </span>
                                        {item}
                                      </a>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </td>
                          <td>
                            {media.observations && (
                              <div className="content">
                                <p className="mb-0">{media.observations}</p>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderPage;